<template>
  <div
    class="doughnut-tile"
    :class="{ 'doughnut-tile--with-background': hasBackground }"
  >
    <v-card v-if="organisations.length" class="doughnut-tile__card">
      <v-app-bar flat color="rgba(0, 0, 0, 0)">
        <div
          @click="redirect()"
          class="doughnut-tile__title h3"
          title="Orgranisation dashboard"
          :class="{ 'white--text': isDarkMode }"
        >
          {{ heading ? heading : client.name }}
          <status-indicator v-for="(app, key) in client.runningApps" :key="key" :title="app.name" class="ml-2 mr-2" status="positive" pulse></status-indicator>
        </div>

        <v-spacer />
        <div @click="editBuild" title="Edit build">
        <apio-icon
          v-if="userBuildRights(client.clientHash)"
          style="curson: pointer;"
          :color="{ blueblack: isDarkMode }"
          name="ui-edit"
          class="ml-4 doughnut-tile__menu-item__icon pointer"
          />
        </div>
        <div
          v-if="userAdminRights(client.clientHash)"
          class="ml-2 doughnut-tile__menu-item__icon"
          style="curson: pointer;"
          @click="editOrg"
          title="Edit organisation"
        ><apio-icon
            :color="{ blueblack: isDarkMode }"
            name="ui-org"
            class="ml-4 doughnut-tile__menu-item__icon"
          />
        </div>
      </v-app-bar>
      <v-card-text class="text-left subtitle-1">
        <div class="d-flex">
          <DoughnutChart
            class="doughnut-tile__chart"
            :organisation-name="organisationName"
            :chart-data="fetchedData"
            @on-receive="update"
            @redirectToEvent="redirect($event)"
          />
          <div class="doughnut-tile__legend">
            <div class="d-flex mb-2">
              <apio-icon
                class="mr-2"
                size="16"
                name="ui-bullet"
                lightModeColour="success"
                darkModeColour="success"
              /><span class="small">Success</span>
            </div>
            <div class="d-flex mb-2">
              <apio-icon
                class="mr-2"
                size="16"
                name="ui-bullet"
                lightModeColour="warning"
                darkModeColour="warning"
              /><span class="small">Warning</span>
            </div>
            <div class="d-flex">
              <apio-icon
                class="mr-2"
                size="16"
                name="ui-bullet"
                lightModeColour="error"
                darkModeColour="error"
              /><span class="small">Error</span>
            </div>
          </div>
        </div>
        <div
          class="doughnut-tile__hr"
          :class="{ white: isDarkMode, apioshade4: !isDarkMode }"
        ></div>
        <div
          class="doughnut-tile__footer"
          :class="{ 'white--text': isDarkMode }"
          v-if="isEmpty"
        >
          No data available...
        </div>
        <div
          class="doughnut-tile__footer"
          :class="{ 'white--text': isDarkMode }"
          v-else
        >
          <div class="mr-4">
            Apps <b>{{ client.app_count || 0 }} </b>
          </div>
          <div class="mr-4">
            Interfaces <b> {{ client.interface_count || 0 }}</b>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { filter, forEach } from "lodash";
import { mapGetters } from "vuex";
import { StatusIndicator } from "vue-status-indicator";

export default {
  name: "DoughnutChartTile",
  components: {
    StatusIndicator,
    DoughnutChart: () => import("@/components/DoughnutChart.vue"),
    // Help: () => import("@/components/Help/Help.vue"),
  },
  props: {
    organisations: {
      type: Array,
    },
    appCount: {
      type: Number,
    },
    interfaceCount: {
      type: Number,
    },
    organisationName: {
      type: String,
    },
    hashes: {
      type: Array,
      required: false,
    },
    client: {
      type: Object,
      required: true,
    },
    hasBackground: {
      type: Boolean,
      required: false,
      default: true,
    },
    heading: {
      type: String,
      required: false,
    },
    timeStart: {
      type: [String, Number],
      required: true,
    },
    timeEnd: {
      type: [String, Number],
      required: true,
    },
    organisation: {
      type: String,
      required: false,
    },
    interfaceMode: {
      type: Boolean,
      required: true,
      default: false,
    },
    mode: {
      type: String,
      required: false,
      default: "organisation",
    },
    running: {
      type: Object,
      required: false,
    },
  },
  computed: {
    ...mapGetters(['userBuildRights', 'userAdminRights']),
    clientAppCount() {
      const clientInfo = filter(this.organisations, (item) => {
        return this.client.clientHash === item.clientHash;
      });
      return clientInfo[0] && clientInfo[0].apps
        ? clientInfo[0].apps.length
        : 0;
    },
    clientInterfaceCount() {
      const clientInfo = filter(this.organisations, (item) => {
        return this.client.clientHash === item.clientHash;
      });

      let interfaceCount = 0;
      if (clientInfo[0] && clientInfo[0].apps) {
        forEach(clientInfo[0].apps, function (app) {
          if (app.interfaces) {
            interfaceCount = interfaceCount + app.interfaces.length;
          }
        });
      }

      return interfaceCount;
    },
    isEmpty() {
      if (this.clientAppCount > 0 || this.clientInterfaceCount > 0)
        return false;
      return false;
    },
    isDarkMode() {
      return this.$vuetify.theme.dark;
    },
    fetchedData() {
      const hasData = this.client.count !== 0 || this.client.error !== 0;
      if (!hasData) {
        return {
          labels: ["No data"],
          datasets: [
            {
              borderWidth: 1,
              backgroundColor: ["#ecedef"],
              data: [true],
            },
          ],
        };
      }
      return {
        labels: ["Successes", "Errors", "Warnings"],
        datasets: [
          {
            borderWidth: 1,
            backgroundColor: ["#61bb46", "#e03a3e", "#febb39"],
            borderColor: ["#61bb46", "#e03a3e", "#febb39"],
            data: [this.client.count, this.client.error, this.client.warning],
          },
        ],
      };
    },
  },
  methods: {
    editBuild() {
      this.$router.push(`/build/?c=${this.client.clientHash}`);
    },
    editOrg() {
      this.$router.push(`/orgs/edit/${this.client.clientHash}`);
    },
    update() {
      this.$router.push(
        `/events-log?f=${this.timeStart}&t=${this.timeEnd}&c=${this.client.clientHash}`
      );
    },
    redirect(label) {
      if (label === "Errors") {
        this.$router.push(
          `/organisation-dashboard?f=${this.timeStart}&t=${this.timeEnd}&q=error&c=${this.client.clientHash}`
        );
      }
      this.$router.push(
        `/organisation-dashboard?f=${this.timeStart}&t=${this.timeEnd}&c=${this.client.clientHash}`
      );
    },
  },
};
</script>

<style lang="scss" src="./DoughnutChartTile.scss" scoped/>
